import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageSquare, Send, Trash2 } from "lucide-react";
import { api, errMsg, getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const ago = (iso) => {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso)) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export const Comments = ({ episodeId }) => {
  const [list, setList] = useState([]);
  const [name, setName] = useState(() => localStorage.getItem("kp_name") || "");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = !!getToken();
  const { user, login } = useAuth();
  const canDelete = (c) => isAdmin || (user && c.user_id && c.user_id === user.user_id);

  const load = () => api.get(`/episodes/${episodeId}/comments`).then((r) => setList(r.data)).catch(() => {});
  useEffect(() => {
    setList([]);
    load();
  }, [episodeId]);

  const submit = async (e) => {
    e.preventDefault();
    const finalName = user ? user.name : name.trim();
    if (!finalName || !text.trim()) return toast.error("Please enter both a name and a comment");
    setBusy(true);
    try {
      const r = await api.post(`/episodes/${episodeId}/comments`, { name: finalName, text: text.trim() });
      if (!user) localStorage.setItem("kp_name", name.trim());
      setList((l) => [r.data, ...l]);
      setText("");
      toast.success("Comment posted");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${c.id}`);
      setList((l) => l.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <section data-testid="comments-section" className="mt-12">
      <div className="flex items-center gap-2 kp-eyebrow mb-5"><MessageSquare size={13} /> Comments · {list.length}</div>
      <form data-testid="comment-form" onSubmit={submit} className="kp-glass p-5 flex flex-col gap-3">
        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-3">
          {user ? (
            <div data-testid="comment-as-user" className="flex items-center gap-2 px-3 border border-white/[0.08] text-sm min-h-[46px]">
              {user.picture && <img src={user.picture} alt="" referrerPolicy="no-referrer" className="w-6 h-6 object-cover" />}
              <span className="truncate">{user.name}</span>
            </div>
          ) : (
            <input data-testid="comment-name-input" className="kp-input" placeholder="Your name" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} required />
          )}
          <input data-testid="comment-text-input" className="kp-input" placeholder="Say something about this episode..." maxLength={600} value={text} onChange={(e) => setText(e.target.value)} required />
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-[0.65rem] font-mono text-[#64748B]">
            {text.length}/600
            {!user && <> · <button type="button" data-testid="comment-google-signin" onClick={login} className="text-[#00F0FF] hover:underline">Sign in with Google</button> to post with your name</>}
          </span>
          <button data-testid="comment-submit" type="submit" disabled={busy} className="kp-btn kp-btn-primary !py-2.5 !px-4 disabled:opacity-60"><Send size={13} /> Post</button>
        </div>
      </form>
      <div data-testid="comment-list" className="mt-6 flex flex-col divide-y divide-white/[0.06]">
        {list.length === 0 && <p data-testid="comments-empty" className="text-[#64748B] text-sm py-6">No comments yet — be the first.</p>}
        {list.map((c, i) => (
          <motion.div key={c.id} data-testid={`comment-${c.id}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 8) * 0.04 }} className="py-4 flex gap-4">
            {c.picture ? <img src={c.picture} alt="" referrerPolicy="no-referrer" className="w-9 h-9 shrink-0 object-cover border border-white/10" /> : <span className="w-9 h-9 shrink-0 bg-[#FF2A55]/15 border border-[#FF2A55]/40 text-[#FF2A55] font-display font-bold text-sm flex items-center justify-center">{c.name[0]?.toUpperCase()}</span>}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 text-xs">
                <span className="font-display font-semibold text-sm truncate">{c.name}</span>
                <span className="font-mono text-[#64748B]">{ago(c.created_at)}</span>
                {canDelete(c) && <button data-testid={`comment-delete-${c.id}`} onClick={() => remove(c)} className="ml-auto p-1 text-[#64748B] hover:text-[#FF2A55] transition-colors" aria-label="Delete"><Trash2 size={13} /></button>}
              </div>
              <p className="text-[#94A3B8] text-sm mt-1.5 leading-relaxed break-words">{c.text}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
};
